import { useLayoutEffect } from 'react';
import { usePrevious } from 'react-use';

import { VisitAndAppointmentStatus } from '@dreryk/egabinet-visit-frontend-api';

import { visitPaths } from '~libs.desktop.shared.paths';

import {
   FILTER_SEPARATOR,
   getPatientPresenceFromSearchParam,
   getVisitAndAppointmentStatusFilterSearchParamValueFromStatuses,
   mapVisitAndAppointmentStatusFilterParamToStatuses,
} from './VisitAndAppointmentStatusField.utils';
import { VisitAndAppointmentStatusFilterParam } from './VisitAndAppointmentStatusField.types';

const VISIT_AND_APPOINTMENT_STATUS_FILTER_STORAGE_KEY = 'visitAndAppointmentStatusFilter';

type VisitAndAppointmentStatusFilterStorage = {
   setFilter: (filter: VisitAndAppointmentStatusFilterParam) => void;
   removeFilter: VoidFunction;
   getStoredStatuses: () => VisitAndAppointmentStatus[];
};

export const visitAndAppointmentStatusFilterStorage: VisitAndAppointmentStatusFilterStorage = (() => {
   const removeFilter = (): void => window.sessionStorage.removeItem(VISIT_AND_APPOINTMENT_STATUS_FILTER_STORAGE_KEY);
   return {
      setFilter: (filter) => {
         if (filter === '') {
            removeFilter();
            return;
         }
         window.sessionStorage.setItem(VISIT_AND_APPOINTMENT_STATUS_FILTER_STORAGE_KEY, filter);
      },
      getStoredStatuses: () => {
         return mapVisitAndAppointmentStatusFilterParamToStatuses(
            window.sessionStorage.getItem(VISIT_AND_APPOINTMENT_STATUS_FILTER_STORAGE_KEY)
         );
      },
      removeFilter: removeFilter,
   };
})();

export const useSyncVisitAndAppointmentStatusFilterParamWithStorage = (
   searchParams: URLSearchParams,
   onSync: (searchParams: URLSearchParams) => void
): void => {
   const visitAndAppointmentStatusFilterParam = searchParams.get(visitPaths.VISIT_AND_APPOINTMENT_STATUS_SEARCH_PARAM);
   const previousVisitAndAppointmentStatusFilterParam = usePrevious(visitAndAppointmentStatusFilterParam);
   const memoizedVisitAndAppointmentStatusFilter = visitAndAppointmentStatusFilterStorage.getStoredStatuses();

   useLayoutEffect(() => {
      if (
         visitAndAppointmentStatusFilterParam &&
         visitAndAppointmentStatusFilterParam !== previousVisitAndAppointmentStatusFilterParam
      ) {
         const statusesFromSearchParam = visitAndAppointmentStatusFilterParam.split(FILTER_SEPARATOR).map(Number);
         const statusesValid = statusesFromSearchParam.every((id) => !isNaN(id));

         if (!statusesValid) {
            searchParams.delete(visitPaths.VISIT_AND_APPOINTMENT_STATUS_SEARCH_PARAM);
            onSync(searchParams);
         }

         if (statusesValid) {
            visitAndAppointmentStatusFilterStorage.setFilter(visitAndAppointmentStatusFilterParam);
         }
      }

      if (memoizedVisitAndAppointmentStatusFilter.length) {
         const statusesFromSearchParam = visitAndAppointmentStatusFilterParam?.split(FILTER_SEPARATOR).map(Number);
         const memoizedStatusesAsSet = new Set(memoizedVisitAndAppointmentStatusFilter);

         if (
            !statusesFromSearchParam ||
            statusesFromSearchParam.some((searchParamValue) => {
               const statusFromSearchParam = getPatientPresenceFromSearchParam(searchParamValue);
               return !statusFromSearchParam || !memoizedStatusesAsSet.has(statusFromSearchParam);
            })
         ) {
            const newFilterParam = getVisitAndAppointmentStatusFilterSearchParamValueFromStatuses(
               memoizedVisitAndAppointmentStatusFilter
            );
            searchParams.set(visitPaths.VISIT_AND_APPOINTMENT_STATUS_SEARCH_PARAM, newFilterParam);
            onSync(searchParams);
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [visitAndAppointmentStatusFilterParam, memoizedVisitAndAppointmentStatusFilter]);
};
